import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useProjectPipelines } from "@/hooks/useProjectPipelines";
import type { PipelineRun } from "@/hooks/useProjectPipelines";
import { pendingGateCount } from "@/lib/pendingGateCount";
import { RunDetailSheet } from "@/components/project/RunDetailSheet";
import { PipelineFilters } from "@/components/project/PipelineFilters";
import { formatDateTime } from "@/lib/date";

const PAGE_SIZE = 25;

interface PipelinesPanelProps {
  projectSlug: string;
}

export function PipelinesPanel({ projectSlug }: PipelinesPanelProps) {
  const { t } = useTranslation();
  const { runs, loading, error } = useProjectPipelines(projectSlug);
  const [selectedRun, setSelectedRun] = useState<PipelineRun | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset pagination when project or filter changes (UAT-4)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [projectSlug, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t("projectPortal.pipelines.loading")}</span>
      </div>
    );
  }

  if (error) {
    return <p className="py-8 text-sm text-destructive">{error}</p>;
  }

  const filtered =
    statusFilter === "all" ? runs : runs.filter((r) => r.status === statusFilter);

  const visibleRuns = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <>
      <div className="mb-3">
        <PipelineFilters value={statusFilter} onChange={setStatusFilter} />
      </div>

      {runs.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">
          {t("projectPortal.pipelines.empty")}
        </p>
      ) : filtered.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">
          {t("projectPortal.pipelines.emptyFiltered")}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("projectPortal.pipelines.table.run")}</TableHead>
              <TableHead>{t("projectPortal.pipelines.table.pipeline")}</TableHead>
              <TableHead>{t("projectPortal.pipelines.table.status")}</TableHead>
              <TableHead>{t("projectPortal.pipelines.table.actor")}</TableHead>
              <TableHead>{t("projectPortal.pipelines.table.started")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRuns.map((run) => {
              const pending = pendingGateCount(run);
              return (
                <TableRow
                  key={run.run_id}
                  className="cursor-pointer"
                  onClick={() => { setSelectedRun(run); }}
                >
                  <TableCell className="font-mono text-xs">
                    {run.run_id.slice(0, 8)}
                  </TableCell>
                  <TableCell>{run.pipeline_name}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      {run.status}
                      {pending > 0 && (
                        <Badge
                          variant="outline"
                          className="border-yellow-500 text-yellow-700"
                        >
                          {t("projectPortal.pipelines.gates.pendingBadge", {
                            count: pending,
                          })}
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{run.actor ?? "—"}</TableCell>
                  <TableCell>{formatDateTime(run.started_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            {t("projectPortal.pipelines.loadMore", "Load more")}
          </Button>
        </div>
      )}

      <RunDetailSheet
        run={selectedRun}
        open={selectedRun !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedRun(null);
        }}
      />
    </>
  );
}
